import { computed, Injectable, signal } from '@angular/core';
import { BehaviorSubject, map, distinctUntilChanged } from 'rxjs';
import { getLocalStorageItem, setLocalStorageItem } from '../utils';

const COMPARISON_STORAGE_KEY = 'brocom_product_comparison';
export const MAX_COMPARE_PRODUCTS = 4;

@Injectable({
  providedIn: 'root',
})
export class ProductComparisonService {
  private readonly productIdsSubject = new BehaviorSubject<string[]>(this.loadProductIds());
  private readonly productIdsState = signal(this.productIdsSubject.value);
  readonly productIds$ = this.productIdsSubject.asObservable();
  readonly selectedProductIds = this.productIdsState.asReadonly();
  readonly selectedCount = computed(() => this.productIdsState().length);

  get productIds(): readonly string[] {
    return this.productIdsState();
  }

  get count(): number {
    return this.selectedCount();
  }

  readonly count$ = this.productIds$.pipe(
    map(productIds => productIds.length),
    distinctUntilChanged()
  );

  contains(productId: string | number): boolean {
    return this.productIdsState().includes(String(productId));
  }

  add(productId: string | number): 'added' | 'already-added' | 'limit-reached' | 'invalid-id' {
    const normalizedId = String(productId ?? '').trim();
    if (!normalizedId) return 'invalid-id';

    const currentIds = this.productIdsState();
    if (currentIds.includes(normalizedId)) return 'already-added';
    if (currentIds.length >= MAX_COMPARE_PRODUCTS) return 'limit-reached';

    this.updateProductIds([...currentIds, normalizedId]);
    return 'added';
  }

  setProducts(productIds: Array<string | number>): void {
    const normalizedIds = [...new Set(
      productIds
        .filter(id => typeof id === 'string' || typeof id === 'number')
        .map(id => String(id).trim())
        .filter(Boolean)
    )].slice(0, MAX_COMPARE_PRODUCTS);
    this.updateProductIds(normalizedIds);
  }

  remove(productId: string | number): void {
    const normalizedId = String(productId);
    this.updateProductIds(this.productIdsState().filter(id => id !== normalizedId));
  }

  clear(): void {
    this.updateProductIds([]);
  }

  private loadProductIds(): string[] {
    try {
      const storedIds = getLocalStorageItem(COMPARISON_STORAGE_KEY);
      if (!Array.isArray(storedIds)) return [];

      return [...new Set(
        storedIds
          .filter(id => typeof id === 'string' || typeof id === 'number')
          .map(id => String(id).trim())
          .filter(Boolean)
      )].slice(0, MAX_COMPARE_PRODUCTS);
    } catch (error) {
      console.error('Unable to read saved product comparisons:', error);
      return [];
    }
  }

  private updateProductIds(productIds: string[]): void {
    setLocalStorageItem(COMPARISON_STORAGE_KEY, productIds);
    this.productIdsState.set(productIds);
    this.productIdsSubject.next(productIds);
  }
}
